import { Dispatch, ReactNode, SetStateAction } from "react";
import { Bounce, Id, toast } from "react-toastify";
import { Exception } from 'sass';
import i18next from "i18next";
import {
    isSupplierUnavailableError,
    SUPPLIER_UNAVAILABLE_TOAST_KEY,
    SUPPLIER_UNAVAILABLE_TOAST_MS,
} from "../../../utils/functions/supplierUnavailable";
import { noteSupplierUnavailableForStatus } from "../../../utils/functions/supplierStatus";
import {
    isNavUnavailableError,
    NAV_UNAVAILABLE_TOAST_KEY,
    NAV_UNAVAILABLE_TOAST_MS,
} from "../../../utils/functions/navUnavailable";
import { isSheetSizeUnavailableError } from "../../../utils/functions/oversizeSheet";
import {
    isUploadRejectedError,
    uploadRejectedToastText,
} from "../../../utils/functions/uploadRejected";
import { trackErrorShown } from "../../../utils/functions/analytics";
import {
    isNotApprovedOrderError,
    NOT_APPROVED_TOAST_MS,
    notApprovedOrderToastText,
} from "../../../utils/functions/orderability";

export function useSuccessToast(title: string, autoClose: number = 5000): Id {
    const id = toast.success(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce
    });

    return id;
}

// errorCode: the HTTP/envelope status when the caller knows it - only the
// code reaches Clarity (event fejl_vist + tag fejlkode, flag
// REACT_APP_CLARITY_EVENTS_ENABLED); the message never does.
// autoClose = false: the toast stays until the user closes it (the
// design-without-MUL refusal, which names the cart lines to remove).
// toastId: a fixed id de-duplicates - a second call while that toast is
// still up REPLACES its text in place (react-toastify 9 silently drops a
// new toast.error whose id is still active, which left a sticky toast
// naming cart lines the user had already removed), and the caller can
// dismiss it by id. Omitted = a generated id, exactly as before.
export function useErrorToast(title: string, autoClose: number | false = 5000, errorCode?: number | string | null, toastId?: Id): Id {
    trackErrorShown(errorCode)
    if (toastId !== undefined && toast.isActive(toastId)) {
        toast.update(toastId, { render: title, autoClose: autoClose });
        return toastId;
    }
    const id = toast.error(title, {
        toastId: toastId,
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

export function useLoadingToast(title: string, autoClose: number = 5000): Id {
    const id = toast.loading(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

export function dissmissToast(id: Id) {
    toast.dismiss(id)
}

// Non-hook-named aliases: the use*Toast helpers above are plain functions,
// but their names trip the react-hooks lint rules when called from effects
// or handlers inside components. New call sites should prefer these.
export function showSuccessToast(title: string, autoClose: number = 5000): Id {
    return useSuccessToast(title, autoClose);
}

export function showErrorToast(title: string, autoClose: number | false = 5000, errorCode?: number | string | null, toastId?: Id): Id {
    return useErrorToast(title, autoClose, errorCode, toastId);
}

export function showLoadingToast(title: string, autoClose: number = 5000): Id {
    return useLoadingToast(title, autoClose);
}

export function showInfoToast(title: string, autoClose: number = 8000): Id {
    const id = toast.info(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

// autoClose = false: the toast stays until the user closes it (the PDF sharp
// gate's "PDF not downloaded" when no dialog can be shown any more).
export function showWarningToast(title: string, autoClose: number | false = 8000): Id {
    const id = toast.warning(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

// Replaces the CONTENT of a loading toast that is still on screen (a loading
// toast never auto-closes; withToasts dismisses it): a text, or an element
// (the PDF sharp gate's text + "Cancel"). Unknown / already dismissed id =
// no-op (react-toastify ignores it).
export function updateLoadingToast(id: Id, content: ReactNode): void {
    toast.update(id, { render: content });
}

interface ContextError { message: String, source: String, statusCode: number }

// Optional extras for withToasts. Omitted = exactly the classic behaviour.
export interface WithToastsOptions<T> {
    // Answers false when the transaction RESOLVED without the success the
    // message claims (the PDF sharp gate stopped before saving and opens its
    // own dialog): no success toast, no error toast.
    showSuccess?: (result: T) => boolean;
    // Hands the caller the loading toast's id so it can update the text
    // while the transaction runs (updateLoadingToast).
    onLoadingToast?: (id: Id) => void;
    // Picks the success text from the transaction's ANSWER (a promise the
    // text makes must hold for what the API actually did). Omitted, or
    // answering undefined = successMessage.
    successMessageOf?: (result: T) => string | undefined;
    // The autoClose (ms) of the success toast WHEN successMessageOf answered
    // a text - a longer text the user should read ("Correction approved - it
    // is being sent ... Ordering opens as soon as ...", 10 s). successMessage
    // keeps the classic 5 s whatever this says. Omitted = the classic 5 s
    // for both.
    successAutoCloseMs?: number;
}

export async function withToasts<T>(
    asyncFunction: () => Promise<T>,
    loadingMessage: string,
    successMessage: string,
    errorMessage: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
    options?: WithToastsOptions<T>
): Promise<T> {
    setIsLoading && setIsLoading(true);
    const toast = useLoadingToast(loadingMessage)
    options?.onLoadingToast?.(toast)

    try {
        const result = await asyncFunction()
        if (!options?.showSuccess || options.showSuccess(result)) {
            const picked = options?.successMessageOf?.(result)
            if (picked !== undefined && options?.successAutoCloseMs !== undefined) {
                useSuccessToast(picked, options.successAutoCloseMs)
            } else {
                useSuccessToast(picked ?? successMessage)
            }
        }
        return result;
    } catch (error) {
        // Flag REACT_APP_SUPPLIER_STATUS_ENABLED: a 503 supplierUnavailable
        // (or the client-side upload timeout) asks the status banner for an
        // immediate re-check - before the toast, whatever the toast flag
        // says. Flag off = no-op.
        noteSupplierUnavailableForStatus(error)
        // Flag REACT_APP_SUPPLIER_UNAVAILABLE_ENABLED: the API's fail-fast
        // 503 supplierUnavailable (or the client-side upload timeout) gets
        // ONE clear toast instead of the compound "Could not …: <message>".
        // Rethrown as every other error, so the caller's own finally still
        // stops its spinner and rolls back its optimistic state. Flag off =
        // isSupplierUnavailableError is always false = exactly today's toast.
        if (isSupplierUnavailableError(error)) {
            useErrorToast(
                i18next.t(SUPPLIER_UNAVAILABLE_TOAST_KEY),
                SUPPLIER_UNAVAILABLE_TOAST_MS,
                503
            )
            throw error
        }
        // Flag REACT_APP_NAV_UNAVAILABLE_ENABLED: the API's fail-fast 503
        // navUnavailable while NAV (the item system) is down or restarting
        // (or the client-side lookup timeout) gets ONE translated toast
        // instead of the compound "Could not place order: <raw NAV text>".
        // Rethrown as every other error, so the caller's own finally still
        // stops its spinner and the cart stays intact. Flag off =
        // isNavUnavailableError is always false = exactly today's toast.
        if (isNavUnavailableError(error)) {
            useErrorToast(
                i18next.t(NAV_UNAVAILABLE_TOAST_KEY),
                NAV_UNAVAILABLE_TOAST_MS,
                503
            )
            throw error
        }
        // Flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED: the API's 409
        // sheetSizeUnavailable at CommitOrder/PlaceOrder is answered by the
        // oversize-sheet dialog (opened by the caller's catch), not by the
        // compound "Could not place order: …" toast. Rethrown as every other
        // error, so the caller's own finally still stops its spinner. Flag
        // off = isSheetSizeUnavailableError is always false = today's toast.
        if (isSheetSizeUnavailableError(error)) {
            throw error
        }
        // Flag REACT_APP_UPLOAD_PENDING_ENABLED: the upload sync path's 422
        // uploadRejected (a deterministic supplier rejection - the API sends
        // it only under UploadRejectMatchV2Enabled) gets ONE translated
        // toast with the same text the pending path's dialog shows, instead
        // of the compound "Could not create logo: <English message>". 8 s:
        // the user has to read what to change in the file. Rethrown as
        // every other error, so the caller's own finally still runs. Flag
        // off = isUploadRejectedError is always false = today's toast.
        if (isUploadRejectedError(error)) {
            useErrorToast(uploadRejectedToastText(error, i18next.t), 8000, 422)
            throw error
        }
        // Flag REACT_APP_DESIGN_ORDERABILITY_GUARD_ENABLED: the API's
        // order-approval guard answers place / create / commit order with
        // 400 designNotApproved / logoNotApproved + the refused lines. ONE
        // readable toast naming the MULs («These items are awaiting approval
        // and were not added or ordered: MUL…») instead of the compound
        // "Failed to place order: {json}". Rethrown as every other error, so
        // the caller's own finally still stops its spinner and the cart stays
        // intact (it is only cleared on success). Flag off =
        // isNotApprovedOrderError is always false = today's toast.
        if (isNotApprovedOrderError(error)) {
            useErrorToast(
                notApprovedOrderToastText(error, i18next.t),
                NOT_APPROVED_TOAST_MS,
                400
            )
            throw error
        }
        const contextError = error as ContextError
        let compoundErrorMessage = errorMessage
        if (contextError && contextError.message) {
            if (errorMessage) {
                compoundErrorMessage += ': ' + contextError.message
            } else {
                compoundErrorMessage = contextError.message as string
            }
        }

        useErrorToast(compoundErrorMessage, 5000, contextError?.statusCode)
        throw error
    } finally {
        dissmissToast(toast)
        setIsLoading && setIsLoading(false)
    }
}